import React from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useCampaignResultsFetch } from '@pages/Traders-Room/Campaign-Results/hooks/useCampaignResultsFetch'

import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'

import { useWindowResize } from '@utils/domUtils'
import { getScrollToCardId } from '@utils/getItemId'

import { ClientRebateCampaignDto, isCampaignClientContest } from '@model/CampaignResultDto'

import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollIntoViewOnPagingEntriesChange,
} from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { Loading } from '@shared/components/Loading/Loading'

import { CampaignResultCard } from './CampaignResultCard'
import { CampaignResultsTable } from './CampaignResultsTable'

export const CampaignResultsPage: React.FC = () => {
  useScrollToTop()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const campaign = useCampaignResultsFetch()
  const isMobile = useWindowResize()

  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = React.useState(false)

  useScrollIntoViewOnPagingEntriesChange(
    ScrollToIds.CampaignResultsHeader,
    isPaginationEntrySelected,
    campaign.query.isLoading,
    setIsPaginationEntrySelected
  )
  useScrollAfterLoad(
    ScrollToIds.CampaignResultsHeader,
    campaign.query.isLoading,
    campaign.query.meta?.pageSize
  )

  const handlePageChange = (pageIndex: number, pageSize?: number) => {
    const prevSize = campaign.query.meta?.pageSize
    if (pageSize && prevSize && pageSize !== prevSize) {
      setIsPaginationEntrySelected(true)
    }

    campaign.query.setPageQuery!({
      ...campaign.query.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const openByRoute = (c: ClientRebateCampaignDto) => {
    const base = isCampaignClientContest(c) ? 'contest' : 'rebate'
    navigate(`${base}/${c.account}/${c.id}`)
  }

  return (
    <Loading showLoadingIcon isLoading={campaign.query.isLoading}>
      <PageSection id={ScrollToIds.CampaignResultsHeader} title={t('Campaigns.Campaigns')} />
      {isMobile ? (
        campaign.query.data.map((x, id) => (
          <CampaignResultCard
            cardId={getScrollToCardId(id, campaign.query.data.length, 'campaign-results')}
            campaignResult={x}
            setSelectedCampaign={openByRoute}
            key={id}
          />
        ))
      ) : (
        <CampaignResultsTable
          campaignResults={campaign.query.data}
          setSelectedCampaign={openByRoute}
        />
      )}

      {campaign.query.meta && (
        <Pagination
          scrollToHeaderId={ScrollToIds.CampaignResultsHeader}
          pageData={campaign.query.meta}
          isLoading={campaign.query.isLoading}
          maxPageSize={100}
          onPageChanged={handlePageChange}
        />
      )}
    </Loading>
  )
}
